import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { createMessPlan, getMessPlans, updateMessPlan, deleteMessPlan, createMessFeeStructure, getMessFeeStructures } from '../../../services/hostelService';
import toast from 'react-hot-toast';

const MessPlans = ({ coachingId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', monthlyCharge: '', description: '' });

  useEffect(() => { loadPlans(); }, [coachingId]);

  const loadPlans = async () => {
    setLoading(true);
    const data = await getMessPlans(coachingId);
    setPlans(data);
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.monthlyCharge) return toast.error('Name and charge required');
    await createMessPlan(coachingId, { ...form, monthlyCharge: parseFloat(form.monthlyCharge) });
    toast.success('Mess plan created');
    setShowForm(false);
    setForm({ name: '', monthlyCharge: '', description: '' });
    loadPlans();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mess plan?')) return;
    await deleteMessPlan(id);
    toast.success('Deleted');
    loadPlans();
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Mess Plans</h2>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} /> Add Plan
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white dark:bg-gray-800 border rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Plan Name (e.g. Full Mess)" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input type="number" value={form.monthlyCharge} onChange={e => setForm({...form, monthlyCharge: e.target.value})} placeholder="Monthly Charge (₹)" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Create</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(p => (
          <div key={p.id} className="bg-white dark:bg-gray-800 border rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-white">{p.name}</h3>
                <p className="text-2xl font-bold text-blue-600 mt-1">₹{p.monthlyCharge?.toLocaleString()}<span className="text-sm font-normal text-gray-500">/month</span></p>
                {p.description && <p className="text-xs text-gray-500 mt-1">{p.description}</p>}
              </div>
              <button onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
            </div>
            <div className="mt-3">
              <span className={`text-xs px-2 py-1 rounded-full ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {p.status || 'active'}
              </span>
            </div>
          </div>
        ))}
        {plans.length === 0 && <p className="text-gray-400 text-sm col-span-full text-center py-8">No mess plans created yet.</p>}
      </div>
    </div>
  );
};

export default MessPlans;
